import { useState, useEffect } from "react";
import MarketLimitButtons from "./components/market-limit-buttons";
import ConfirmButton from "./components/confirm-button";
import { useBuyBotActions } from "./hooks/use-buy-bot-hook";
import { useToken } from "../../hooks/use-token";
import { useBuySellBotTokenContext } from "../../providers/buy-sell-token-provider";
import { useWallet, BotWalletSupportedChain } from "@/providers/wallet";
import { useBotBuyTokenMutation } from "@/api/bot/bot-buy-token";
import { useBotLimitBuyMutation } from "@/api/bot/bot-limit-buy";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatNativeCurrency } from "@/utils/currency";
import { BOT_GET_ORDERS_QUERY_KEY } from "@/api/bot/bot-get-orders";
import { BOT_GET_TOKEN_INFO_QUERY_KEY } from "@/api/bot/bot-get-token-info";
import { BOT_GET_USER_INFO_QUERY_KEY } from "@/api/bot/bot-get-user-info";
import { SCREENER_QUERY_KEY } from "@/api/token-screener/screener-tokens";
import AutoSellButtonsV2 from "./components/auto-sell-buttons-v2";
import SlippageToggleInputV2 from "./slippage-toggle-input-v2";
import BalanceNativeTokenInputV2 from "./components/balance-native-token-input-v2";
import YouReceiveTokenInputV2 from "./components/you-receive-token-input-v2";
import BuySellOptions from "./components/buy-sell-options";
import LimitInputV2 from "./components/limit-input-v2";
import { TRANSACTIONS_BOT_QUERY_KEY } from "@/api/transactions/transactions-bot";

interface BuyBotActionsV2Props {
  onSuccess?: () => void;
}

const BuyBotActionsV2 = ({ onSuccess }: BuyBotActionsV2Props) => {
  const token = useToken();
  const buy = useBuyBotActions();
  const buySellContext = useBuySellBotTokenContext();
  const { userId, appPubKey, currentWallet: wallet } = useWallet();

  const {
    mutate: buyMutate,
    isPending: isBuyPending,
    isSuccess: isBuySuccess,
  } = useBotLimitBuyMutation();
  const {
    mutate: buyMutateInstant,
    isPending: isBuyPendingInstant,
    isSuccess: isBuySuccessInstant,
  } = useBotBuyTokenMutation();
  const { isConnected } = useWeb3Auth();
  const queryClient = useQueryClient();
  const [showBuySuccess, setShowBuySuccess] = useState(false);
  const [isAutoSell, setIsAutoSell] = useState(false);

  useEffect(() => {
    if (isBuySuccess || isBuySuccessInstant) {
      setShowBuySuccess(true);
      setTimeout(() => setShowBuySuccess(false), 3000);
      onSuccess?.();
    }
  }, [isBuySuccess, isBuySuccessInstant, onSuccess]);

  const handleBuy = () => {
    const youPayFormattedAmount = formatNativeCurrency(
      buy.state.youPayNative,
      wallet.botChainId,
    );
    if (youPayFormattedAmount === "") return;
    const botChainId = token?.tokenId?.startsWith("0x")
      ? BotWalletSupportedChain.ETH
      : BotWalletSupportedChain.SOL;
    const botChainSymbol =
      botChainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";
    if (buy.state.isInstant) {
      buyMutateInstant(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            walletAddress: wallet.address,
            amount: youPayFormattedAmount,
            slippage: buySellContext.isBuySlippageAuto
              ? undefined
              : Number(buySellContext.buySlippage),
            isAutoSellOn: isAutoSell,
          },
          notifyParams: {
            tokenSymbol: token?.symbol,
            youPayNative: buy.state.youPayNative,
            chainSymbol: botChainSymbol,
            youReceive: buy.state.youReceive,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [SCREENER_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    } else {
      buyMutate(
        {
          params: {
            userId,
            appPubKey,
            chainId: botChainId,
            tokenAddress: token?.address ?? "",
            walletAddress: wallet.address,
            amount: youPayFormattedAmount,
            slippage: buySellContext.isBuySlippageAuto
              ? undefined
              : Number(buySellContext.buySlippage),
            triggerPrice: buy.state.triggerPrice,
            isAutoSellOn: isAutoSell,
          },
          notifyParams: {
            youReceive: buy.state.youReceive,
            tokenSymbol: token?.symbol,
            youPayNative: buy.state.youPayNative,
            chainSymbol: botChainSymbol,
            limitPrice: buy.state.triggerPrice,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_ORDERS_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [SCREENER_QUERY_KEY],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
              exact: false,
            });
          },
        },
      );
    }
  };

  return (
    <div className="flex flex-col mt-[0.938rem]">
      <div className="flex flex-col gap-y-[0.3125rem]">
        <MarketLimitButtons
          isInstant={buy.state.isInstant}
          onMarketClick={() => buy.handlers.handleInstantChange(true)}
          onLimitClick={() => buy.handlers.handleInstantChange(false)}
        />
        <BalanceNativeTokenInputV2
          youPayNative={buy.state.youPayNative}
          onYouPayNativeChange={buy.handlers.handleYouPayNativeChange}
          balancePercent={buy.state.balancePercent}
          onBalancePercentChange={buy.handlers.handleBalancePercentChange}
        />
        <BuySellOptions
          value={buy.state.youPayNative}
          onOptionSelect={(v) => buy.handlers.handleYouPayNativeChange(v)}
        />
        <YouReceiveTokenInputV2
          youReceive={buy.state.youReceive}
          youReceiveUSD={buy.state.youPayUSD}
          onYouReceiveChange={buy.handlers.handleYouReceiveChange}
          onYouReceiveUSDChange={buy.handlers.handleYouPayUSDChange}
        />
      </div>
      {!buy.state.isInstant && (
        <LimitInputV2
          triggerPrice={buy.state.triggerPrice}
          limit={buy.state.limit}
          onLimitChange={buy.handlers.handleLimitChange}
        />
      )}

      <AutoSellButtonsV2
        isAutoSell={isAutoSell}
        setIsAutoSell={setIsAutoSell}
      />

      <SlippageToggleInputV2
        isAuto={buySellContext.isBuySlippageAuto}
        value={buySellContext.buySlippage}
        onAutoChange={() =>
          buySellContext.setIsBuySlippageAuto((prev) => !prev)
        }
        onValueChange={(v) => buySellContext.setBuySlippage(v)}
      />

      <ConfirmButton
        enabled={
          isConnected &&
          !isBuyPending &&
          !isBuyPendingInstant &&
          !showBuySuccess
        }
        onClick={handleBuy}
        loading={isBuyPending || isBuyPendingInstant}
        success={showBuySuccess}
        className="mt-[1.25rem] w-full h-[1.875rem] hover:bg-green75Bg"
        background="green2Bg"
        gradient="green2"
        activeStyle="green2"
        hover="none"
      />
    </div>
  );
};

export default BuyBotActionsV2;
